import {
  defaultEndpoint
} from "../chunk-MM6UCDX3.js";

// src/client/createTRPCNuxtClient.ts
import { createTRPCClientProxy, createTRPCUntypedClient } from "@trpc/client";
import { createTRPCFlatProxy } from "@trpc/server";

// src/client/decorationProxy.ts
import { createTRPCRecursiveProxy } from "@trpc/server";
import { useAsyncData } from "nuxt/app";
import { getCurrentInstance, isRef, onScopeDispose, shallowRef, toRaw, toValue } from "vue";

// src/client/getQueryKey.ts
import { hash } from "ohash";
function getQueryKeyInternal(path, input) {
  return input === void 0 ? path : `${path}-${hash(input || "")}`;
}
function getQueryKey(procedureOrRouter, ..._params) {
  const [input] = _params;
  const path = procedureOrRouter._def().path;
  const dotPath = path.join(".");
  return getQueryKeyInternal(dotPath, input);
}
function getMutationKeyInternal(path) {
  return getQueryKeyInternal(path, void 0);
}
function getMutationKey(procedure) {
  const path = procedure._def().path;
  const dotPath = path.join(".");
  return getMutationKeyInternal(dotPath);
}

// src/client/decorationProxy.ts
function isRefOrGetter(val) {
  return isRef(val) || typeof val === "function";
}
function createAbortController(trpc) {
  let controller;
  if (trpc?.abortOnUnmount) {
    if (getCurrentInstance()) {
      onScopeDispose(() => {
        controller?.abort?.();
      });
    }
    controller = typeof AbortController !== "undefined" ? new AbortController() : {};
  }
  return controller;
}
function createNuxtProxyDecoration(name, client) {
  return createTRPCRecursiveProxy((opts) => {
    const args = opts.args;
    const pathCopy = [name, ...opts.path];
    const lastArg = pathCopy.pop();
    const path = pathCopy.join(".");
    const [input, otherOptions] = args;
    if (lastArg === "_def") {
      return {
        path: pathCopy
      };
    }
    if (lastArg === "useQuery") {
      const { trpc, queryKey: customQueryKey, ...asyncDataOptions } = otherOptions || {};
      const controller = createAbortController(trpc);
      const queryKey = customQueryKey || getQueryKeyInternal(path, toValue(input));
      const watch = isRefOrGetter(input) ? [...asyncDataOptions.watch || [], input] : asyncDataOptions.watch;
      return useAsyncData(queryKey, () => client[path].query(toValue(input), {
        signal: controller?.signal,
        ...trpc
      }), {
        ...asyncDataOptions,
        watch
      });
    }
    if (lastArg === "useMutation") {
      const { trpc, mutationKey: customMutationKey, ...asyncDataOptions } = otherOptions || {};
      const input2 = shallowRef(null);
      const controller = createAbortController(trpc);
      const mutationKey = customMutationKey || getMutationKeyInternal(path);
      const asyncData = useAsyncData(mutationKey, () => client[path].mutate(toRaw(input2.value), {
        signal: controller?.signal,
        ...trpc
      }), {
        ...asyncDataOptions,
        lazy: false,
        server: false,
        immediate: false
      });
      async function mutate(value) {
        input2.value = value;
        await asyncData.execute();
        return toRaw(asyncData.data.value);
      }
      Object.assign(asyncData, { mutate });
      return asyncData;
    }
    return client[path][lastArg](...args);
  });
}

// src/client/createTRPCNuxtClient.ts
function createTRPCNuxtClient(opts) {
  const client = createTRPCUntypedClient(opts);
  const proxy = createTRPCClientProxy(client);
  const decoratedClient = createTRPCFlatProxy((key) => {
    return createNuxtProxyDecoration(key, proxy);
  });
  return decoratedClient;
}

// src/client/links.ts
import {
  httpBatchLink as _httpBatchLink,
  httpBatchStreamLink as _httpBatchStreamLink,
  httpLink as _httpLink
} from "@trpc/client";
import { useRequestHeaders } from "nuxt/app";
function isFetchError(error) {
  return error instanceof Error && error.name === "FetchError";
}
async function customFetch(input, init) {
  return globalThis.$fetch.raw(input.toString(), init).catch((e) => {
    if (isFetchError(e) && e.response) {
      return e.response;
    }
    throw e;
  }).then((response) => ({
    ...response,
    headers: response.headers,
    json: () => Promise.resolve(response._data)
  }));
}
function createDefaultLinkOptions(pickHeaders) {
  const headers = useRequestHeaders(pickHeaders);
  return {
    url: defaultEndpoint,
    headers() {
      return headers;
    },
    fetch: customFetch
  };
}
function httpLink(opts) {
  return _httpLink({
    ...createDefaultLinkOptions(opts?.pickHeaders),
    ...opts
  });
}
function httpBatchLink(opts) {
  return _httpBatchLink({
    ...createDefaultLinkOptions(opts?.pickHeaders),
    ...opts
  });
}
function httpBatchStreamLink(opts) {
  return _httpBatchStreamLink({
    ...createDefaultLinkOptions(opts?.pickHeaders),
    ...opts
  });
}
export {
  createTRPCNuxtClient,
  getMutationKey,
  getQueryKey,
  httpBatchLink,
  httpBatchStreamLink,
  httpLink
};
